import { Injectable } from '@angular/core';

import {
  GasNetworkDamageModel, GasNetworkTariffModel, OrderConfirmModel,
  OrderInvoiceModel,
  OrderModel,
  OrderOnRentDamageModel,
  OrderService as ApiOrderService,
  ResponseModel, TrailerModel, UserService as ApiUserService
} from '@core/api';
import { Observable } from 'rxjs/internal/Observable';
import { filter, finalize, map } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { GasNetworksService as ApiGasNetworksService } from '@core/api/api/gasNetworks.service';
import { User } from '@core/models/user';
import { UserService } from '@core/auth';
import { OrderTimeService } from '@core/components/services/order-time.service';
import { UserModel } from '@core/api/model/userModel';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { OrderTokenService, SessionToken } from '@accounts/operator/services/order-token.service';

import * as moment from 'moment';
import { TransferTokenService } from '@accounts/operator/services/transfer-token.service';
import { GasStationsService } from '@admin/services/gas-stations.service';

export const ROUTE_PATH_MAP = {
  [OrderModel.StatusEnum.Created]: 'verification',
  [OrderModel.StatusEnum.Verified]: 'defects',
  [OrderModel.StatusEnum.Paid]: 'act',
  [OrderModel.StatusEnum.InRent]: 'return',
  [OrderModel.StatusEnum.Acceptance]: 'return'
};

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  constructor(
    private apiOrderService: ApiOrderService,
    private apiUserService: ApiUserService,
    private apiGasNetworksService: ApiGasNetworksService,
    private orderTimeService: OrderTimeService,
    private orderTokenService: OrderTokenService,
    private transferTokenService: TransferTokenService,
    public gasStationsService: GasStationsService,
  ) {
  }

  getTariffName(type: GasNetworkTariffModel.TypeEnum, hours: number): string {
    return this.orderTimeService.getStringOrderTime(type, hours);
  }

  getOrders(body: HttpParams): Observable<ResponseModel<OrderModel[]>> {
    return this.apiOrderService.orderGet(body);
  }

  getOrder(id: number): Observable<OrderModel> {
    return this.apiOrderService.orderIdGet(id)
      .pipe(map(res => ({ ...res.data, ...res.meta })));
  }

  getOrderConfirmationCode(id: number): Observable<any> {
    return this.apiOrderService.getOrderConfirmationCode(id);
  }

  downloadTransferAct(id: number): any {
    return this.apiOrderService.downloadTransferAct(id);
  }

  downloadReturnAct(id: number): any {
    return this.apiOrderService.downloadReturnAct(id);
  }

  createOrder(body): Observable<OrderModel> {
    return this.apiOrderService.orderPost(body)
      .pipe(map(res => res.data));
  }

  rejectOrder(id: number, comment: string): Observable<null> {
    return this.apiOrderService.orderIdPut(
      id,
      new HttpParams()
        .set('comment', comment)
        .set('close_comment', comment)
        .set('status', OrderModel.StatusEnum.Rejected)
    )
      .pipe(map(res => res.data));
  }

  verificationOrder(id: number): Observable<null> {
    return this.apiOrderService.orderIdPut(
      id,
      new HttpParams()
        .set('status', OrderModel.StatusEnum.Verified)
    )
      .pipe(map(res => res.data));
  }

  getExtraInvoices(id: number): Observable<OrderInvoiceModel[]> {
    return this.apiOrderService.orderIdInvoiceGet(id)
      .pipe(
        map(res => res.data.filter(invoice => invoice.type === OrderInvoiceModel.TypeEnum.ExtraInvoice))
      );
  }

  getInvoices(id: number): Observable<OrderInvoiceModel[]> {
    return this.apiOrderService.orderIdInvoiceGet(id)
      .pipe(
        map(res => res.data.filter(invoice => invoice.type === OrderInvoiceModel.TypeEnum.Invoice))
      );
  }

  invoicePayment(id: number, invoice: OrderInvoiceModel): Observable<OrderInvoiceModel> {
    return this.apiOrderService.orderIdInvoicePost(invoice, id)
      .pipe(map(res => res.data));
  }

  paymentConfirmation(id: number): Observable<null> {
    return this.apiOrderService.orderIdPut(
      id,
      new HttpParams()
        .set('status', OrderModel.StatusEnum.Paid)
    )
      .pipe(map(res => res.data));
  }


  acceptTrailer(id: number): Observable<null> {
    return this.apiOrderService.orderIdPut(
      id,
      new HttpParams()
        .set('status', OrderModel.StatusEnum.Acceptance)
    )
      .pipe(map(res => res.data));
  }

  confirmCloseOrder(id: number, phone: string) {
    return this.apiOrderService.orderIdConfirmPost({
      type: OrderConfirmModel.TypeEnum.Close
    }, id)
      .pipe(map(this.orderTokenService.save(phone)));
  }

  confirmTransferTrailer(id: number, phone: string) {
    return this.apiOrderService.orderIdConfirmPost({
      type: OrderConfirmModel.TypeEnum.Transfer
    }, id)
      .pipe(map(this.transferTokenService.save(phone)));
  }

  acceptanceOrder (id: number, body): Observable<null> {
    return this.apiOrderService.orderIdPut(
        id,
        {
          'status': OrderModel.StatusEnum.Acceptance,
          ...body
        })
        .pipe(map(res => res.data));
  }

  closeOrder (id: number, body): Observable<null> {
    return this.apiOrderService.orderIdPut(
      id,
      {
        'status': OrderModel.StatusEnum.Closed,
        ...body
      })
      .pipe(map(res => res.data));
  }

  startOrder(id: number, body): Observable<null> {
    return this.apiOrderService.orderIdPut(
      id,
      {
        'status': OrderModel.StatusEnum.InRent,
        ...body
      })
      .pipe(map(res => res.data));
  }

  getDefects(id: number): Observable<OrderOnRentDamageModel[]> {
    return this.apiOrderService.orderIdOnRentDamageGet(id)
      .pipe(map(res => res.data));
  }

  createDefect(id: number, defect: OrderOnRentDamageModel): Observable<OrderOnRentDamageModel> {
    return this.apiOrderService.orderIdOnRentDamagePost(defect, id)
      .pipe(map(res => res.data));
  }

  deleteDefect(id: number, defectId: number): Observable<ResponseModel<null>> {
    return this.apiOrderService.orderIdOnRentDamageDamageIdDelete(id, defectId);
  }

  getDamageList(id: number): Observable<GasNetworkDamageModel[]> {
    return this.apiGasNetworksService.gasNetworkIdDamageGet(id)
      .pipe(map(res => res.data));
  }

  getUserById(id: number): Observable<UserModel> {
    return this.apiUserService.userIdGet<UserModel>(id)
      .pipe(map(res => res.data));
  }

  updateExpiredTime(id: number): Observable<OrderModel> {
    return this.apiOrderService.orderIdExpiredTimePut(id)
      .pipe(map(res => res.data));
  }

  updateAdapterPassed(id: number, adapter: boolean): Observable<OrderModel> {
    return this.apiOrderService.orderIdAdapterPassedPut(id, adapter)
      .pipe(map(res => res.data));
  }

  isCreated(order: OrderModel): boolean {
    return order && order.status === OrderModel.StatusEnum.Created;
  }

  isCancelable(order: OrderModel): boolean {
    return order && [
      OrderModel.StatusEnum.Created,
      OrderModel.StatusEnum.Verified,
      OrderModel.StatusEnum.Paid
    ].includes(order.status);
  }

  isAllowedNextStatus(order: OrderModel): boolean {
    return [
      OrderModel.StatusEnum.Created,
      OrderModel.StatusEnum.Paid,
      OrderModel.StatusEnum.Verified,
      OrderModel.StatusEnum.InRent,
      OrderModel.StatusEnum.Acceptance
    ].includes(order.status);
  }

  getRoutePathByStatus(status: OrderModel.StatusEnum) {
    return ROUTE_PATH_MAP[status];
  }
}

export class OrderDataSource {

  private damagesSubject = new BehaviorSubject<GasNetworkDamageModel[]>([]);
  damages = this.damagesSubject.asObservable();

  private damagesLoadingSubject = new BehaviorSubject<boolean>(false);
  damagesLoading = this.damagesLoadingSubject.asObservable();

  constructor(
    private orderService: OrderService,
    private userService: UserService,
    private order: OrderModel,
  ) {
    this.damagesLoadingSubject.next(true);

    if (this.order && this.order.gasStation && this.order.gasStation.network_id) {
      this.orderService.getDamageList(this.order.gasStation.network_id)
        .pipe(finalize(() => this.damagesLoadingSubject.next(false)))
        .subscribe(data => {
          this.damagesSubject.next(data);
        });
    }

    if (this.order && this.order.user && this.order.user.id) {
      this.orderService.getUserById(this.order.user.id)
        .subscribe(data => {
          this.order.user = data;
        });
    }
  }

  get damagesValue(): GasNetworkDamageModel[] {
    return this.damagesSubject.value;
  }

  get id(): number {
    return this.order && this.order.id;
  }

  get expiredAt(): number {
    return this.order && this.order.expired_at;
  }

  get operator(): User {
    return this.userService.currentUserValue;
  }

  get stationName(): string {
    return this.order && this.order.gasStation && this.order.gasStation.name;
  }

  get networkName(): string {
    let networkName = '';
     if(this.stationNetworkId){
      networkName = this.orderService.gasStationsService.getGasNetworkName(this.stationNetworkId)
    };
    return networkName
  }

  get stationNetworkId(): number {
    return this.order && this.order.gasStation && this.order.gasStation.network_id;
  }

  get operatorFullName(): string {
    return `${this.operator && this.operator.last_name} ${this.operator && this.operator.first_name} ${this.operator && this.operator.patronymic_name}`;
  }

  get renterFullName(): string {
    return `${this.order && this.order.user && this.order.user.last_name} ${this.order && this.order.user && this.order.user.first_name} ${this.order && this.order.user && this.order.user.patronymic_name}`;
  }

  get renterPassportNumber(): string {
    return this.order && this.order.user && this.order.user.passport && this.order.user.passport.number;
  }

  get renterPassportDate(): string {
    if (this.order && this.order.user && this.order.user.passport && this.order.user.passport.issued_at) {
      return moment(this.order.user.passport.issued_at).format('DD.MM.YYYY');
    }

    return void 0;
  }

  get trailerNumber(): string {
    return this.order && this.order.trailer && this.order.trailer.registration_number;
  }

  get trailerModel(): string {
    return `${this.order && this.order.trailer && this.order.trailer.brand} ${this.order && this.order.trailer && this.order.trailer.model}`;
  }

  get trailerLoad(): string {
    return String(this.order && this.order.trailer && this.order.trailer.load_capacity);
  }

  get adapterPassed(): boolean {
    return this.order && this.order.adapter_passed;
  }

  get adapterReturned(): boolean {
    return this.order && this.order.adapter_returned;
  }

  get trailerDamage(): boolean {
    return this.order && this.order.trailer && this.order.trailer.damage;
  }

  get trailerSpareWheel(): boolean {
    return this.order && this.order.trailer && this.order.trailer.spare_wheel;
  }

  get trailerTent(): boolean {
    switch (this.order && this.order.trailer && this.order.trailer.type) {
      case TrailerModel.TypeEnum.WithoutTent:
        return false;
      case TrailerModel.TypeEnum.WithTent:
        return true;
    }
  }

  get transportBrand(): string {
    return this.order && this.order.transport && this.order.transport.brand
  }

  get transportModel(): string {
    return this.order && this.order.transport && this.order.transport.model
  }

  get transportStateNumber(): string {
    return this.order && this.order.transport && this.order.transport.state_number
  }

  get transportRegistrationNumber(): string {
    return this.order && this.order.transport && this.order.transport.number
  }

  get userCountryCode(): string {
    return this.order && this.order.user && this.order.user.country_code;
  }

  get userPhoneNumber(): string {
    return this.order && this.order.user && this.order.user.phone_number;
  }

  get getTariffName(): string {
    return this.orderService.getTariffName(this.order.tariff_type, this.order.rent_hours);
  }

  get returnPriceToAnotherPoint(): number {
    if (!this.order.returnGasStation) {
      return 0;
    }

    const value = (this.order as any).return_price_to_another_point;

    if (value !== undefined && value !== null) {
      return Number(value);
    }

    return 0;
  }

  get cost() {
    return this.order && this.order.cost;
  }

  get extraCost(): number {
    return this.order && this.order.extraCost;
  }

  get extraHours(): number {
    return this.order && this.order.extraHours;
  }

  get returnDamages(): GasNetworkDamageModel[] {
    return this.order && this.order.returnDamages;
  }

  get status(): OrderModel.StatusEnum {
    return this.order.status;
  }

  get isOrderAccepted(): boolean{
    return this.order.status === OrderModel.StatusEnum.Acceptance;
  }

  get rentFromStation(): string {
    let address = '';

    if (this.order && this.order.gasStation && this.order.gasStation.address) {
      address = this.order.gasStation.address + ', ' + this.order.gasStation.name;
    }

    return address;
  }

  get rentToStation(): string {
    if (!this.order || !this.order.returnGasStation || !this.order.returnGasStation.address) {
      return this.rentFromStation;
    }

    return this.order.returnGasStation.address + ', ' + this.order.returnGasStation.name;
  }

}
